import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Numeric, Tooltip, Typography } from '@blade/ui-kit'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { AntiMevShield } from '@root/features/trade/trade-settings/ui/desktop'
import { useTranslation } from '@root/shared/localization'
import { PetrolIcon, RunnerIcon, SharingHandIcon } from '@root/shared/ui-kit'

import { useGasSettingsValues } from '../../common/use-gas-settings-values'

type Props = {
  onSettingsOpen: () => void
}

export const TradingGas: React.FC<Props> = ({ onSettingsOpen }) => {
  const { t } = useTranslation()

  const [tradeSettings] = useAtom(tradeSettingsAtom)

  const { antiMevEnabled, bribe, priorityFee, slippage } =
    useGasSettingsValues(tradeSettings)

  return (
    <Root>
      <Group>
        <Tooltip content={t('trading.order.gas-priority-fee-tooltip')}>
          <Item onClick={onSettingsOpen}>
            <PetrolIcon size={16} />
            <Numeric value={priorityFee ?? 0} preset="currency-amount-short" />
          </Item>
        </Tooltip>

        <Tooltip content={t('trading.order.slippage-percentage-tooltip')}>
          <Item onClick={onSettingsOpen}>
            <RunnerIcon size={16} />
            <span>
              <Numeric value={slippage} preset="percents" />%
            </span>
          </Item>
        </Tooltip>
        {bribe ? (
          <Tooltip content={t('trading.order.bribe-tooltip')}>
            <Item onClick={onSettingsOpen}>
              <SharingHandIcon size={16} />
              <Numeric value={bribe} preset="amount-max-5-digits" />
            </Item>
          </Tooltip>
        ) : undefined}
      </Group>
      <Mev onClick={onSettingsOpen}>
        <MevShieldIcon size={22} active={antiMevEnabled ?? false} />
      </Mev>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  overflow: hidden;

  grid-template-columns: auto 1fr;

  align-items: center;

  padding-top: 3px;
  justify-self: end;
`

const Group = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  justify-content: start;
`

const Item = styled(Typography).attrs({
  variant: 'body5-medium',
  component: 'div',
})`
  color: var(--text-11);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  align-items: center;
  gap: 2px;
  padding-inline: 7px;

  border-left: 1px solid var(--backgroundA-4);
  &:first-child {
    border-left: none;
  }
`

const Mev = styled.div`
  cursor: pointer;
  /* 9px — no typography token */
  font-size: 9px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 181.818% */

  display: grid;
  align-items: center;
  justify-content: end;
  grid-template-columns: 1fr auto auto;
  justify-items: end;
`

const MevShieldIcon = styled(AntiMevShield)`
  margin-inline-end: 3px;
`
